import { COMPOSER_BOX_ATTR } from "src/components/llm/components/compact-chat-input-layout";
import { ComposerFilePill } from "src/components/llm/components/composer-file-pill";
import { useCallback, useId, useRef, useState } from "react";
import { useInView } from "motion/react";
import {
  Box,
  Button,
  Checkbox,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  Column,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  ProgressBar,
  Row,
  Text,
  TextWithTooltip,
  Tooltip,
  HtImage,
} from "@hightouch/htui";
import prettyBytes from "pretty-bytes";
import { useComposerThumbnail } from "src/components/llm/components/use-composer-thumbnail";
import { UploadProgressList } from "src/components/llm/components/upload-progress-list";
import { AttachmentTypeTile } from "src/components/llm/components/attachment-type-tile";
import { openFilePreview } from "src/components/llm/components/file-preview-card";
import { useImageGalleryOverlay } from "src/components/llm/components/full-screen-image-overlay";
import type { ComposerAttachments } from "src/components/llm/components/use-composer-attachments";
import {
  ATTACHMENT_CATEGORY_GROUPS,
  attachmentCategory,
  type AttachmentCategory,
} from "src/components/llm/utils/attachment-presentation";
import {
  groupFileItems,
  limitGroupItems,
} from "src/components/llm/components/composer-file-groups";
import { attachmentSize } from "src/components/llm/utils/attachment-validation";
import {
  composerImageSrc,
  type ComposerFile,
} from "src/components/llm/utils/file-processing";
import type { AttachmentUpload } from "src/components/llm/context/file-context";
import { LoadMoreSentinel } from "src/components/load-more-sentinel";

const INLINE_FILE_LIMIT = 10;
// Rows mounted per scroll step. Each row costs about 3ms to mount, so a large drop opens a page at a time.
const ROW_PAGE = 50;

const composerWidth = (reference: {
  getBoundingClientRect: () => { width: number };
}) => {
  const composer =
    reference instanceof Element
      ? reference.closest(`[${COMPOSER_BOX_ATTR}]`)
      : null;
  return (composer ?? reference).getBoundingClientRect().width;
};

type Props = Pick<
  ComposerAttachments,
  "files" | "uploadsInFlight" | "removeAttachments" | "retryAttachment"
>;
type FileItem = {
  id: string;
  fileName: string;
  category: AttachmentCategory;
  sizeBytes?: number;
  folderPath?: string;
  file?: ComposerFile;
  upload?: AttachmentUpload;
};

export const ComposerFiles = (props: Props) => {
  // Unmount the popover when empty so a new selection starts closed.
  if (props.files.length + props.uploadsInFlight.size === 0) return null;
  return <FilePopover {...props} />;
};

const FilePopover = ({
  files,
  uploadsInFlight,
  removeAttachments,
  retryAttachment,
}: Props) => {
  const [isRowHovered, setIsRowHovered] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rowLimit, setRowLimit] = useState(ROW_PAGE);
  const loadMoreRows = useCallback(
    () => setRowLimit((limit) => limit + ROW_PAGE),
    [],
  );
  const closeRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [measuredComposerWidth, setMeasuredComposerWidth] = useState(0);
  const isNarrowComposer = measuredComposerWidth <= 480;
  const titleId = useId();
  const items: FileItem[] = files.map((file) => {
    if (!file.id)
      throw new Error(
        "Composer files must have an attachment ID from the file store.",
      );
    return {
      id: file.id,
      fileName: file.fileName,
      category: attachmentCategory(file.mimeType, file.fileName),
      sizeBytes: attachmentSize(file),
      folderPath: file.folderPath ?? undefined,
      file,
    };
  });
  for (const [id, upload] of uploadsInFlight) {
    items.push({
      id,
      fileName: upload.fileName,
      category:
        upload.mimeType !== undefined
          ? attachmentCategory(upload.mimeType, upload.fileName)
          : "unknown",
      sizeBytes: upload.sizeBytes,
      folderPath: upload.folderPath,
      upload,
    });
  }
  const groups = groupFileItems(items);
  // The trigger stacks type tiles whichever way the list is grouped.
  const categories = ATTACHMENT_CATEGORY_GROUPS.filter((group) =>
    items.some((item) => item.category === group.category),
  );
  const selected = items.filter((item) => selectedIds.has(item.id));
  const allSelected = selected.length === items.length;
  const sizeKnown = items.every((item) => item.sizeBytes !== undefined);
  const totalBytes = items.reduce(
    (total, item) => total + (item.sizeBytes ?? 0),
    0,
  );
  const sizeLabel = sizeKnown ? prettyBytes(totalBytes) : undefined;
  const countLabel = `${items.length} ${items.length === 1 ? "file" : "files"}`;
  const galleryFiles = files.flatMap((file) => {
    const imageSrc = composerImageSrc(file);
    return imageSrc ? [{ id: file.id, imageSrc, fileName: file.fileName }] : [];
  });
  const { openGallery, overlay } = useImageGalleryOverlay(galleryFiles);

  const close = () => {
    setIsOpen(false);
    setSelectedIds(new Set());
    setRowLimit(ROW_PAGE);
  };
  const remove = (ids: string[]) => {
    removeAttachments(ids);
    if (items.length - ids.length <= INLINE_FILE_LIMIT) {
      close();
      return;
    }
    setSelectedIds(
      (previous) => new Set([...previous].filter((id) => !ids.includes(id))),
    );
    // A removed row cannot retain focus; keep keyboard navigation inside the panel.
    closeRef.current?.focus();
  };

  const preview = (item: FileItem) => {
    const index = galleryFiles.findIndex((file) => file.id === item.id);
    close();
    if (index >= 0) openGallery(index);
    else if (item.file) openFilePreview(item.file);
  };

  return (
    <>
      <Box width="100%" onClick={(event) => event.stopPropagation()}>
        {uploadsInFlight.size > 0 &&
          (!isOpen || items.length <= INLINE_FILE_LIMIT) && (
            <Box pb={2}>
              <UploadProgressList
                uploads={uploadsInFlight}
                files={files}
                onRemove={(id) => remove([id])}
                onRetry={retryAttachment}
              />
            </Box>
          )}
        {items.length <= INLINE_FILE_LIMIT && files.length > 0 && (
          <Row
            aria-label="Attached file previews"
            onMouseEnter={() => setIsRowHovered(true)}
            onMouseLeave={() => setIsRowHovered(false)}
          >
            {files.map((file, index) => (
              <ComposerFilePill
                key={file.id}
                file={file}
                index={index}
                isRowHovered={isRowHovered}
                isPopoverOpen={hoveredId === file.id}
                onPillEnter={() => setHoveredId(file.id ?? null)}
                onPillLeave={() => setHoveredId(null)}
                onRemove={() => file.id && remove([file.id])}
                onImageClick={() => {
                  const item = items.find((item) => item.id === file.id);
                  if (item) preview(item);
                }}
              />
            ))}
          </Row>
        )}
        {items.length > INLINE_FILE_LIMIT && (
          <Popover
            isOpen={isOpen}
            onOpen={() => {
              if (triggerRef.current)
                setMeasuredComposerWidth(composerWidth(triggerRef.current));
              setIsOpen(true);
            }}
            onClose={close}
            placement={isNarrowComposer ? "top" : "top-start"}
            gutter={32}
            isLazy
            lazyBehavior="unmount"
            initialFocusRef={closeRef}
          >
            <PopoverTrigger>
              <Box
                ref={triggerRef}
                as="button"
                type="button"
                width="100%"
                aria-label={`Manage ${countLabel}`}
                borderRadius="2xl"
                _focusVisible={{ boxShadow: "outline" }}
              >
                <Row
                  align="center"
                  gap={2}
                  pl={1.5}
                  pr={2}
                  py={1.5}
                  maxWidth="100%"
                  width="fit-content"
                  borderRadius="2xl"
                  borderWidth="hairline"
                  borderColor="base.border"
                  bg="white"
                  boxShadow="buttonShadowLight"
                  _hover={{ bg: "base.lightBackground" }}
                  _focusVisible={{ boxShadow: "outline" }}
                >
                  <Row flexShrink={0}>
                    {categories.slice(0, 3).map((group, index) => (
                      <Box
                        key={group.category}
                        ml={index === 0 ? 0 : -1}
                        borderWidth="hairline"
                        borderColor="white"
                        borderRadius="md"
                      >
                        <AttachmentTypeTile
                          category={group.category}
                          size="md"
                        />
                      </Box>
                    ))}
                  </Row>
                  <Row align="center" gap={1} minWidth={0}>
                    <Text
                      size="sm"
                      fontWeight="semibold"
                      color="text.secondary"
                      isTruncated
                    >
                      {countLabel}
                    </Text>
                    {sizeLabel && (
                      <Text size="sm" color="text.tertiary" isTruncated>
                        · {sizeLabel}
                      </Text>
                    )}
                  </Row>
                  <Box
                    as={isOpen ? ChevronUpIcon : ChevronDownIcon}
                    boxSize={5}
                    flexShrink={0}
                    aria-hidden
                  />
                </Row>
              </Box>
            </PopoverTrigger>
            <Portal>
              <PopoverContent
                aria-labelledby={titleId}
                width={`${isNarrowComposer ? measuredComposerWidth : 360}px`}
                maxWidth="calc(100vw - 32px)"
                maxHeight="min(480px, calc(100dvh - 32px))"
                overflow="hidden"
                onClick={(event) => event.stopPropagation()}
              >
                <Row
                  px={6}
                  py={3}
                  gap={2}
                  align="center"
                  borderBottomWidth="hairline"
                  borderColor="base.divider"
                  flexShrink={0}
                >
                  <Text id={titleId} size="lg" fontWeight="semibold">
                    {countLabel}
                  </Text>
                  <Box flex={1}>
                    {sizeLabel && (
                      <Text size="sm" color="text.secondary">
                        {sizeLabel}
                      </Text>
                    )}
                  </Box>
                  <IconButton
                    ref={closeRef}
                    type="button"
                    icon={CloseIcon}
                    aria-label="Close files"
                    size="sm"
                    variant="tertiary"
                    onClick={close}
                  />
                </Row>
                <Column p={3} gap={2} overflowY="auto" minHeight={0}>
                  <Row gap={3} px={1.5} py={1.5} align="center">
                    <Checkbox
                      aria-label="Select all files"
                      isChecked={allSelected}
                      isIndeterminate={selected.length > 0 && !allSelected}
                      onChange={(event) =>
                        setSelectedIds(
                          new Set(
                            event.target.checked
                              ? items.map((item) => item.id)
                              : [],
                          ),
                        )
                      }
                    />
                    <Box flex={1}>
                      <Text size="sm" fontWeight="medium">
                        {selected.length} selected
                      </Text>
                    </Box>
                    <Button
                      type="button"
                      icon={CloseIcon}
                      size="sm"
                      onClick={() =>
                        remove(
                          (selected.length ? selected : items).map(
                            (item) => item.id,
                          ),
                        )
                      }
                    >
                      {selected.length ? "Remove selected" : "Clear all"}
                    </Button>
                  </Row>
                  {limitGroupItems(groups, rowLimit).map((group) => (
                    <Column key={group.key} gap={1}>
                      <Row gap={2} align="baseline" minWidth={0}>
                        <Box flex={1} minWidth={0}>
                          <TextWithTooltip size="sm" color="text.tertiary">
                            {group.label}
                          </TextWithTooltip>
                        </Box>
                        <Text size="sm" color="text.tertiary">
                          {group.total}
                        </Text>
                      </Row>
                      <Column
                        as="ul"
                        aria-label={group.label}
                        listStyleType="none"
                        m={0}
                        p={0}
                      >
                        {group.items.map((item) => (
                          <FileRow
                            key={item.id}
                            item={item}
                            isSelected={selectedIds.has(item.id)}
                            onSelect={(checked) =>
                              setSelectedIds((previous) => {
                                const next = new Set(previous);
                                if (checked) next.add(item.id);
                                else next.delete(item.id);
                                return next;
                              })
                            }
                            onRemove={() => remove([item.id])}
                            onRetry={() => retryAttachment(item.id)}
                            onPreview={
                              item.file &&
                              (item.file.previewUrl || item.file.base64Data)
                                ? () => preview(item)
                                : undefined
                            }
                          />
                        ))}
                      </Column>
                    </Column>
                  ))}
                  {rowLimit < items.length && (
                    <LoadMoreSentinel
                      // Remounts per page, so a sentinel still in view after a page loads fires again.
                      key={rowLimit}
                      onVisible={loadMoreRows}
                    >
                      <Box height={8} aria-hidden />
                    </LoadMoreSentinel>
                  )}
                </Column>
              </PopoverContent>
            </Portal>
          </Popover>
        )}
      </Box>
      {overlay}
    </>
  );
};

const FileRow = ({
  item,
  isSelected,
  onSelect,
  onRemove,
  onRetry,
  onPreview,
}: {
  item: FileItem;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onRemove: () => void;
  onRetry: () => void;
  onPreview?: () => void;
}) => (
  <Column
    as="li"
    px={1.5}
    py={1}
    gap={1}
    borderRadius="lg"
    _hover={{ bg: "base.lightBackground" }}
  >
    <Row align="center" gap={3}>
      <Checkbox
        aria-label={`Select ${item.fileName}`}
        isChecked={isSelected}
        onChange={(event) => onSelect(event.target.checked)}
      />
      <FileThumbnail item={item} size={6} onPreview={onPreview} />
      <Box flex={1} minWidth={0}>
        {onPreview ? (
          <Box
            as="button"
            type="button"
            aria-label={`Preview ${item.fileName}`}
            onClick={onPreview}
            width="100%"
            textAlign="left"
            borderRadius="sm"
            _focusVisible={{ boxShadow: "outline" }}
          >
            <TextWithTooltip size="sm">{item.fileName}</TextWithTooltip>
          </Box>
        ) : (
          <TextWithTooltip size="sm">{item.fileName}</TextWithTooltip>
        )}
      </Box>
      {item.sizeBytes !== undefined && (
        <Text size="sm" color="text.tertiary" whiteSpace="nowrap">
          {prettyBytes(item.sizeBytes)}
        </Text>
      )}
      <IconButton
        type="button"
        icon={CloseIcon}
        aria-label={`Remove ${item.fileName}`}
        size="xs"
        variant="tertiary"
        onClick={onRemove}
      />
    </Row>
    {item.upload && (
      <Column pl={7} gap={1}>
        {item.upload.status === "failed" ? (
          <Row align="center" gap={2}>
            <Box flex={1}>
              <Text size="sm" color="text.danger">
                {item.upload.error}
              </Text>
            </Box>
            <Button
              type="button"
              size="sm"
              variant="tertiary"
              onClick={onRetry}
            >
              Retry
            </Button>
          </Row>
        ) : (
          <>
            <Text size="sm" color="text.secondary">
              {item.upload.status === "queued" ? "Queued" : "Uploading…"}
            </Text>
            <ProgressBar
              aria-label={`Uploading ${item.fileName}`}
              value={
                item.upload.total > 0
                  ? Math.min(
                      100,
                      Math.round(
                        (item.upload.loaded / item.upload.total) * 100,
                      ),
                    )
                  : 0
              }
            />
          </>
        )}
      </Column>
    )}
  </Column>
);

const FileThumbnail = ({
  item,
  size,
  onPreview,
}: {
  item: FileItem;
  size: number;
  onPreview?: () => void;
}) => {
  // Thumbnails decode the whole image, so only rows near the viewport build one.
  const ref = useRef<HTMLDivElement>(null);
  const isNearViewport = useInView(ref, { margin: "200px" });
  const imageSrc = useComposerThumbnail(item.file, isNearViewport);
  const thumbnail = imageSrc ? (
    <HtImage
      src={imageSrc}
      alt={item.fileName}
      boxSize={size}
      objectFit="cover"
      borderRadius="md"
    />
  ) : (
    <AttachmentTypeTile category={item.category} size="md" />
  );
  const canPreview =
    onPreview && item.file && (item.file.previewUrl || item.file.base64Data);
  const trigger = canPreview ? (
    <Box
      as="button"
      type="button"
      flexShrink={0}
      aria-label={`Open ${item.fileName}`}
      onClick={onPreview}
      borderRadius="md"
      _focusVisible={{ boxShadow: "outline" }}
    >
      {thumbnail}
    </Box>
  ) : (
    <Box flexShrink={0}>{thumbnail}</Box>
  );
  const preview = imageSrc ? (
    <Tooltip
      placement="top"
      message={
        <HtImage
          src={imageSrc}
          alt={item.fileName}
          width={60}
          maxHeight={60}
          objectFit="contain"
          borderRadius="md"
        />
      }
    >
      {trigger}
    </Tooltip>
  ) : (
    trigger
  );
  return (
    <Box ref={ref} boxSize={size} flexShrink={0}>
      {preview}
    </Box>
  );
};
